import { Button, Modal, Tooltip } from "@/components";
import { FormInput } from "@/components/Form";
import Popover from "@/components/Popover";
import { cn } from "@/utils";
import { Editor, useEditorState } from "@tiptap/react";
import EmojiPicker, { Categories, EmojiStyle } from "emoji-picker-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FaHighlighter } from "react-icons/fa6";
import {
  MdAddLink,
  MdFormatBold,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatStrikethrough,
  MdFormatUnderlined,
  MdOutlineEmojiEmotions,
} from "react-icons/md";
import { FontSizeSelect } from "./FontSizeSelect";

export const MenuBar = ({ editor }: { editor: Editor }) => {
  const [open, setOpen] = useState(false);

  if (!editor) {
    return null;
  }

  // ✅ Insert không tạo paragraph mới
  const handleSelectEmoji = (emojiData: string) => {
    editor
      .chain()
      .focus()
      .insertContent(emojiData, {
        // updateSelection: false, // Không update selection
        // parseOptions: {
        //   preserveWhitespace: "full", // Preserve whitespace
        // },
      })
      .run();
  };

  const menus = useMemo(() => {
    if (!editor) return [];

    return [
      {
        title: "Bold",
        icon: (
          <Tooltip
            content={
              <div className="text-center">
                <div>太字</div>
                <code>Ctrl+B</code>
              </div>
            }
          >
            <MdFormatBold />
          </Tooltip>
        ),
        action: () => editor.chain().focus().toggleBold().run(),
      },
      {
        title: "Italic",
        icon: (
          <Tooltip
            content={
              <div className="text-center">
                <div>斜体</div>
                <code>Ctrl+I</code>
              </div>
            }
          >
            <MdFormatItalic />
          </Tooltip>
        ),
        action: () => editor.chain().focus().toggleItalic().run(),
      },
      {
        title: "Strike",
        icon: (
          <Tooltip
            content={
              <div className="text-center">
                <div>取り消し線</div>
                <code>Ctrl+Shift+S</code>
              </div>
            }
          >
            <MdFormatStrikethrough />
          </Tooltip>
        ),
        action: () => editor.chain().focus().toggleStrike().run(),
      },
      {
        title: "Underline",
        icon: (
          <Tooltip
            content={
              <div className="text-center">
                <div>下線</div>
                <code>Ctrl+U</code>
              </div>
            }
          >
            <MdFormatUnderlined />
          </Tooltip>
        ),
        action: () => editor.chain().focus().toggleUnderline().run(),
      },
      {
        title: "Highlight",
        icon: (
          <Tooltip
            content={
              <div className="text-center">
                <div>ハイライト</div>
                <code>Ctrl+Shift+H</code>
              </div>
            }
          >
            <FaHighlighter />
          </Tooltip>
        ),
        action: () => editor.chain().focus().toggleHighlight().run(),
      },
      {
        title: "numberedList",
        icon: (
          <Tooltip
            content={
              <div className="text-center">
                <div>番号付きリスト</div>
                <code>Ctrl+Shift+7</code>
              </div>
            }
          >
            <MdFormatListNumbered />
          </Tooltip>
        ),
        action: () => editor.chain().focus().toggleOrderedList().run(),
      },
      {
        title: "bulletList",
        icon: (
          <Tooltip
            content={
              <div className="text-center">
                <div>箇条書きリスト</div>
                <code>Ctrl+Shift+8</code>
              </div>
            }
          >
            <MdFormatListBulleted />
          </Tooltip>
        ),
        action: () => editor.chain().focus().toggleBulletList().run(),
      },
      {
        title: "setLink",
        icon: (
          <Tooltip content="リンクを設定">
            <MdAddLink />
          </Tooltip>
        ),
        action: () => {
          // open modal
          setOpen(true);
        },
      },
    ];
  }, [editor]);

  const editorStates = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!editor) return null;

      return [
        editor.isActive("bold"),
        editor.isActive("italic"),
        editor.isActive("strike"),
        editor.isActive("underline"),
        editor.isActive("highlight"),
        editor.isActive("orderedList"),
        editor.isActive("bulletList"),
      ];
    },
  });

  return (
    <div className="tiptap_menu flex items-center flex-wrap gap-2 px-2 py-1 border-t">
      {/* Fontsize select  */}
      <FontSizeSelect editor={editor} />

      {menus.map((menu, index) => (
        <button
          key={index}
          onClick={menu.action}
          className={cn(
            "text-light_secondary font-semibold rounded p-1",
            editorStates?.[index] ? "bg-grey-300" : ""
          )}
          type="button"
        >
          {menu.icon}
        </button>
      ))}

      {/* Emoji picker */}
      <Popover
        side="top"
        align="end"
        trigger={
          <button
            type="button"
            className="text-light_secondary font-semibold flex items-center justify-center"
          >
            <Tooltip content="絵文字を挿入">
              <MdOutlineEmojiEmotions />
            </Tooltip>
          </button>
        }
        enableCloseOnClick={false}
        asChild
      >
        <EmojiPicker
          onEmojiClick={(data) => {
            // Handle select emoji
            handleSelectEmoji(data.emoji);
          }}
          emojiVersion="0.6" // tiptap emoji extension hỗ trợ tối đa version 14.1
          skinTonesDisabled
          emojiStyle={EmojiStyle.NATIVE}
          searchPlaceHolder="検索"
          categories={[
            { name: "おすすめ", category: Categories.SUGGESTED },
            { name: "スマイリーと人物", category: Categories.SMILEYS_PEOPLE },
            { name: "動物と自然", category: Categories.ANIMALS_NATURE },
            { name: "食べ物と飲み物", category: Categories.FOOD_DRINK },
            { name: "活動", category: Categories.ACTIVITIES },
            { name: "旅行と場所", category: Categories.TRAVEL_PLACES },
            { name: "物", category: Categories.OBJECTS },
            // { name: "シンボル", category: Categories.SYMBOLS },
          ]}
        />
      </Popover>

      {/* Link modal  */}
      <ModalLinkInput editor={editor} open={open} setOpen={setOpen} />
    </div>
  );
};

const ModalLinkInput = ({
  editor,
  open,
  setOpen,
}: {
  editor: Editor;
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      name: "",
      url: "",
    },
  });

  // Set focus vào name khi mở modal và pre-fill với selected text
  useEffect(() => {
    if (open && editor) {
      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to);

      if (selectedText) {
        setValue("name", selectedText);
      }
    }
  }, [open, editor, setValue]);

  const handleInsertLink = useCallback(
    (data: { name: string; url: string }) => {
      if (!data.url || !data.name) return;

      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to);

      // Update selected text khi name thay đổi
      const hasSelection = from !== to;
      // Replace selected text với name mới
      if (hasSelection) {
        editor
          .chain()
          .focus()
          .deleteSelection()
          .insertContent(data.name)
          .setTextSelection({ from: from, to: from + data.name.length })
          .run();
      }

      if (selectedText) {
        // Set link cho text đã được update
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: data.url, target: "_blank" })
          .run();

        // editor.chain().insertContent(" ").run();
        // Nếu link đang ở cuối thì append thêm 1 space
        // const newTo = from + data.name.length;
        // if (newTo === editor.state.doc.content.size) {
        //   editor.chain().focus().insertContent(" ").run();
        // }
      } else {
        // Insert text mới với link
        editor
          .chain()
          .focus()
          .insertContent(
            `<a href="${data.url}" target="_blank">${data.name}</a> `
          )
          .run();
      }

      setOpen(false);
      reset();
    },
    [editor, reset, setOpen]
  );

  return (
    <Modal
      open={open}
      onOpenChange={setOpen}
      title="リンクを挿入"
      className="max-w-[620px]"
    >
      <form className="space-y-4" onSubmit={handleSubmit(handleInsertLink)}>
        <FormInput
          control={control}
          name="name"
          required
          label="名前"
          autoFocus // Tự động focus khi modal mở
        />

        <FormInput control={control} name="url" required label="URL" />

        <div className="flex items-center gap-2 justify-end">
          <Button
            type="button"
            buttonType="text"
            onClick={() => setOpen(false)}
          >
            キャンセル
          </Button>
          <Button type="submit" buttonType="primary">
            挿入
          </Button>
        </div>
      </form>
    </Modal>
  );
};
